import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Shield, Building, Target, Edit, Eye, Rocket, BarChart3, Zap, CheckCircle, ArrowRight, Globe, Search, PlusCircle, LineChart, Layers, Award, Gift, Sparkles, Clock, Database, BadgeCheck, Brain, FileText, Share, Upload, Lock, Users, Briefcase, TrendingUp, Star, DollarSign, Gauge, Network, Filter, Settings, Cpu, Radar, MapPin, Languages, Workflow, Code, RefreshCw, BookOpen, Verified, ChartLine, Lightbulb, Timer, ScanLine, CloudLightning, Puzzle } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const HowItWorks = () => {
  const steps = [
    {
      step: 1,
      title: "Connect Your Google Ads Account",
      description: "OAuth 2.0 authentication with Google Ads API v19 and Manager (MCC) support for agencies",
      icon: <Shield className="h-12 w-12 text-primary" />,
      details: [
        "Google Ads API v19 integration with secure OAuth 2.0 flow",
        "Manager (MCC) account support with customer ID selection",
        "Individual and multi-account management for agencies",
        "60-day token refresh with automatic reconnection",
        "95% connection success rate with error recovery"
      ],
      technical: [
        "Bearer token authentication with encrypted storage",
        "Real-time connection status verification",
        "Automatic account hierarchy detection for MCC users",
        "Complete audit trail for all account actions"
      ],
      mockup: "Google OAuth consent screen showing 'Upfixe ads has this access' with Google Ads permissions",
      security: "Bank-level security with encrypted credential storage and no permanent Google data retention."
    },
    {
      step: 2,
      title: "AI Brand Analysis & Value Proposition", 
      description: "Upfixe Ads AI analyzes your website to create structured brand messaging documents",
      icon: <Brain className="h-12 w-12 text-primary" />,
      details: [
        "Automated website content extraction and analysis",
        "AI-generated value proposition with WHO/WHAT/WHY/WHERE structure",
        "Brand voice detection and tone identification",
        "15 seed keywords generated from brand analysis",
        "Brand limits: 1 for free users, unlimited for paid subscriptions"
      ],
      technical: [
        "Upfixe Ads AI with specialized campaign strategy prompts",
        "PostgreSQL storage with comprehensive brand schema",
        "Real-time content crawling and extraction",
        "Competitive positioning analysis and USP generation"
      ],
      mockup: "Website URL input with AI analysis progress and structured goal preview",
      feature: "Our AI creates comprehensive brand profiles in seconds, not hours of manual documentation."
    },
    {
      step: 3,
      title: "Create Campaign with Simple Form",
      description: "Intuitive campaign setup form with all essential parameters in one streamlined interface",
      icon: <PlusCircle className="h-12 w-12 text-primary" />,
      details: [
        "Campaign Name: Custom naming with brand prefix suggestions",
        "Brand Selection: Choose from your pre-configured brand profiles",
        "Landing Page URL: Direct integration with URL validation",
        "Target CPA: AI-suggested cost per acquisition based on industry data",
        "Daily Budget: Smart budget recommendations with spend forecasting",
        "Geography: Real-time location targeting with radius options",
        "Target Language: Multi-language support with cultural adaptation",
        "Campaign Type: Search, Display, or Performance Max options"
      ],
      technical: [
        "Real-time form validation with Google Ads API compliance checks",
        "Dynamic budget calculator with CPA-based recommendations",
        "Geographic autocomplete with Google Places API integration",
        "Brand profile integration with automatic value proposition loading"
      ],
      mockup: "Campaign creation form with all parameters visible and validation indicators",
      benefit: "Complete campaign setup in under 2 minutes vs. 30+ minutes navigating Google Ads interface."
    },
    {
      step: 4,
      title: "Budget-Filtered Keyword Sourcing",
      description: "AI generates 150+ keywords, instantly discarding any where the CPC mathematically violates your Target CPA",
      icon: <Search className="h-12 w-12 text-primary" />,
      details: [
        "URL Keyword Seeds: 15 core budget-checked seeds from website analysis",
        "URL Expansion: 150+ keywords from Google Ads API landing page analysis",
        "Strict CPA Filtering: Instant removal of any keyword deemed too expensive",
        "Real search volume, CPC, and competition data for all kept keywords",
        "Intelligent deduplication and volume validation"
      ],
      technical: [
        "ProductionGeoLanguageResolver for real-time geo/language targeting",
        "Dynamic Google Ads API constant resolution (geoTargetConstants/languageConstants)",
        "Relevance scoring with AI-powered keyword filtering",
        "Performance metrics: CPC, search volume, competition levels"
      ],
      mockup: "Keyword generation pipeline with real-time progress and source breakdown",
      highlight: "150+ keywords researched from multiple sources in 2-5 minutes vs. 3-4 hours manual research."
    },
    {
      step: 5,
      title: "Budget-Smart Ad Group Allocation",
      description: "AI organizes surviving keywords into 5 strategically differentiated ad groups to protect ROI",
      icon: <Puzzle className="h-12 w-12 text-primary" />,
      details: [
        "High ROI Solutions: Our core budget-safe group (Strict CPA ≤ target)",
        "High Volume Searches: Safe keywords with strong search volume",  
        "Native Language Focus: Original intent-driven keywords from goal generation",
        "AI Thematic Group 1: First AI-analyzed semantic cluster",
        "AI Thematic Group 2: Second thematic grouping for coverage"
      ],
      technical: [
        "Strategic keyword allocation algorithm with performance prediction",
        "Used vs. unused keyword management for manual optimization",
        "CPA-based filtering and volume threshold calculations",
        "AI semantic clustering for thematic group creation"
      ],
      mockup: "5-column ad group structure with keyword allocation matrix and performance indicators",
      strategy: "Each ad group targets different search intents and performance goals for optimal campaign structure."
    },
    {
      step: 6,
      title: "AI Ad Copy Generation & Optimization",
      description: "75+ unique ad combinations generated per campaign with psychological triggers and brand voice",
      icon: <Edit className="h-12 w-12 text-primary" />,
      details: [
        "15 headlines + 5 descriptions per ad group (75 total ad combinations)",
        "Character limit validation with Google Ads compliance",
        "Keyword integration and call-to-action optimization",
        "Brand voice consistency across all ad copy variants",
        "Negative keyword management with campaign-level exclusions"
      ],
      technical: [
        "Upfixe Ads AI with conversion-optimized copywriting prompts",
        "Real-time character counting and validation",
        "Psychological trigger integration and localization support",
        "Brand name integration in minimum 3 headlines per group"
      ],
      mockup: "Ad copy editor with character counters, preview cards, and negative keyword manager",
      benefit: "Professional copywriter-quality ads generated instantly with full editing control and optimization suggestions."
    },
    {
      step: 7,
      title: "Real-Time Google Ads API Publishing",
      description: "Direct API integration creates complete campaigns with 95% success rate and real-time tracking",
      icon: <Rocket className="h-12 w-12 text-primary" />,
      details: [
        "Google Ads API v19 direct integration with dynamic constants",
        "Complete campaign structure: budgets, ad groups, keywords, and ads",
        "Real-time geo/language targeting with resolved constants",
        "Step-by-step progress tracking with error recovery",
        "Immediate Google Ads dashboard access with campaign links"
      ],
      technical: [
        "Dynamic customer ID resolution for MCC accounts",
        "Batch API operations for efficient campaign creation",
        "Automatic retry logic with intelligent error handling",
        "Complete audit trail with Google Ads resource logging"
      ],
      mockup: "Publishing progress interface with step-by-step status and Google Ads confirmation",
      power: "2-5 minute campaign creation vs. 10+ hours manual setup - campaigns go live instantly with professional structure."
    },
    {
      step: 8,
      title: "Automated Performance Analytics",
      description: "AI-powered reporting with optimization insights and automated weekly performance summaries",
      icon: <BarChart3 className="h-12 w-12 text-primary" />,
      details: [
        "Real-time Google Ads performance data synchronization",
        "Automated weekly report generation with AI insights",
        "Keyword-level performance analysis and optimization suggestions",
        "Budget optimization recommendations and spend allocation insights",
        "ROI tracking with conversion analysis and performance alerts"
      ],
      technical: [
        "Google Ads API data pipeline with real-time sync",
        "AI-powered performance analysis and trend detection",
        "Automated alert system for budget overspend and low performance",
        "Custom dashboard with interactive charts and drill-down capabilities"
      ],
      mockup: "Performance dashboard with trend charts, optimization cards, and automated insights",
      insight: "Actionable optimization insights delivered automatically - no manual analysis or spreadsheet management required."
    },
  ];

  const advancedFeatures = [
    {
      title: "Supercharged Bulk Generation",
      description: "Website scanner discovers up to 50 pages with 3-level depth crawling for comprehensive campaign portfolios",
      icon: <ScanLine className="h-10 w-10 text-primary" />,
      benefit: "Create 10+ campaigns simultaneously from website analysis",
      technical: "Intelligent page discovery with relevance scoring and automated campaign generation"
    },
    {
      title: "Real-Time Geo/Language Resolution",
      description: "ProductionGeoLanguageResolver converts 'London, England' to geoTargetConstants/1006886 dynamically",
      icon: <MapPin className="h-10 w-10 text-primary" />,
      benefit: "Precise targeting without hardcoded mappings or manual lookups",
      technical: "Google Ads API constant resolution with 3-step disambiguation process"
    },
    {
      title: "Multi-Language Campaign Adaptation",
      description: "Cultural adaptation with native language copywriting and local market insights",
      icon: <Languages className="h-10 w-10 text-primary" />,
      benefit: "Launch global campaigns with locally optimized content and targeting",
      technical: "Dynamic language constant resolution with cultural context integration"
    },
    {
      title: "Strategic Keyword Allocation Algorithm",
      description: "CPA-based filtering with volume thresholds and AI semantic clustering for optimal ad group structure",
      icon: <Filter className="h-10 w-10 text-primary" />,
      benefit: "Maximize ROI with data-driven keyword distribution across 5 strategic groups",
      technical: "Performance prediction with used/unused keyword management"
    },
    {
      title: "Google Ads API v19 Direct Integration",
      description: "Real-time campaign creation with batch operations and automatic retry logic",
      icon: <Database className="h-10 w-10 text-primary" />,
      benefit: "95% success rate with instant campaign publishing and error recovery",
      technical: "OAuth 2.0 with MCC support and complete audit trail logging"
    },
    {
      title: "AI Performance Analytics Pipeline",
      description: "Automated insights with trend detection and optimization recommendations",
      icon: <ChartLine className="h-10 w-10 text-primary" />,
      benefit: "Weekly reports with actionable optimization insights automatically delivered",
      technical: "Real-time data sync with AI-powered performance analysis and alerts"
    }
  ];

  const technicalBenefits = [
    {
      title: "No Manual Keyword Research",
      description: "Our AI conducts comprehensive keyword research across multiple sources",
      time: "Saves 3-4 hours per campaign",
      icon: <Search className="h-8 w-8 text-primary" />
    },
    {
      title: "Eliminates Learning Curve",
      description: "Professional campaign structure from day one, no Google Ads expertise required",
      time: "Weeks of learning avoided",
      icon: <Brain className="h-8 w-8 text-primary" />
    },
    {
      title: "10+ Hours Saved Per Campaign",
      description: "From research to launch, automated workflow handles complex campaign creation",
      time: "10+ hours saved",
      icon: <Clock className="h-8 w-8 text-primary" />
    },
    {
      title: "Continuous Optimization",
      description: "AI-powered suggestions for ongoing campaign performance improvements",
      time: "Ongoing time savings",
      icon: <TrendingUp className="h-8 w-8 text-primary" />
    }
  ];

  const userTypes = [
    {
      type: "For Agencies",
      benefits: [
        "Manage multiple clients from a single dashboard",
        "Create client-ready reports automatically",
        "Scale your ad management without adding staff",
        "Impress clients with data-driven campaigns"
      ],
      icon: <Award className="h-12 w-12 text-primary" />
    },
    {
      type: "For In-House Marketers",
      benefits: [
        "Launch campaigns faster with less hands-on work",
        "Get AI guidance without needing to be an ads expert",
        "Maintain brand consistency across all campaigns",
        "Optimize spend with intelligent budget allocation"
      ],
      icon: <BadgeCheck className="h-12 w-12 text-primary" />
    },
    {
      type: "For Startups",
      benefits: [
        "Go from zero to professional-grade campaigns quickly",
        "Focus your limited budget on keywords that actually work",
        "Get expert-level ads without hiring specialists",
        "Scale your ad efforts as you grow"
      ],
      icon: <Sparkles className="h-12 w-12 text-primary" />
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>How Google Ads Campaign Automation Works | Step-by-Step Guide | Upfixe</title>
        <meta name="description" content="Learn how Upfixe automates Google Ads campaigns in 8 simple steps. From account connection to performance monitoring, complete workflow explained." />
        <meta name="keywords" content="Google Ads automation, campaign creation process, how to use Google Ads, automated marketing" />
        <meta property="og:title" content="How Google Ads Campaign Automation Works | Step-by-Step Guide | Upfixe" />
        <meta property="og:description" content="Learn how Upfixe automates Google Ads campaigns in 8 simple steps. From account connection to performance monitoring, complete workflow explained." />
        <link rel="canonical" href="https://upfixe.com/how-it-works" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">Complete Google Ads Automation</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              From Setup to Success in
              <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent"> 8 Simple Steps</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Our AI-powered workflow automates Google Ads campaign creation from account connection to performance monitoring. 
              No expertise required, professional results guaranteed.
            </p>
            
            {/* Key value propositions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="flex items-center justify-center bg-primary/10 px-6 py-3 rounded-full">
                <Clock className="h-5 w-5 text-primary mr-2" />
                <span className="font-medium">Save 10+ hours per campaign</span>
              </div>
              <div className="flex items-center justify-center bg-primary/10 px-6 py-3 rounded-full">
                <Target className="h-5 w-5 text-primary mr-2" />
                <span className="font-medium">150+ AI-researched keywords</span>
              </div>
              <div className="flex items-center justify-center bg-primary/10 px-6 py-3 rounded-full">
                <Rocket className="h-5 w-5 text-primary mr-2" />
                <span className="font-medium">One-click publishing</span>
              </div>
            </div>
          </div>
          
          {/* User Type Tabs */}
          <div className="mb-16">
            <Tabs defaultValue="process" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="process">How It Works</TabsTrigger>
                <TabsTrigger value="user-types">Who It's For</TabsTrigger>
              </TabsList>
              
              <TabsContent value="process" className="mt-6">
                {/* Steps Section - Complete Google Ads Workflow */}
                <div className="max-w-7xl mx-auto space-y-12">
                  {steps.map((step, index) => (
                    <div key={index} className="relative">
                      <div className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                        {/* Content Side */}
                        <div className="flex-1 space-y-6">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-xl font-bold">
                              {step.step}
                            </div>
                            <div>
                              <h2 className="text-3xl font-bold mb-2">{step.title}</h2>
                              <p className="text-lg text-muted-foreground">{step.description}</p>
                            </div>
                          </div>
                          
                          {/* Feature List */}
                          <div className="bg-card border border-primary/10 rounded-lg p-6">
                            <ul className="space-y-3">
                              {step.details.map((detail, i) => (
                                <li key={i} className="flex items-start gap-3">
                                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                  <span className="text-sm">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Special Feature Highlight */}
                          {step.security && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Lock className="h-5 w-5 text-green-600 mt-0.5" />
                                <div>
                                  <p className="font-medium text-green-800 mb-1">Security First</p>
                                  <p className="text-sm text-green-700">{step.security}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {step.feature && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                                <div>
                                  <p className="font-medium text-blue-800 mb-1">AI-Powered Feature</p>
                                  <p className="text-sm text-blue-700">{step.feature}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {step.highlight && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Star className="h-5 w-5 text-amber-600 mt-0.5" />
                                <div>
                                  <p className="font-medium text-amber-800 mb-1">Time Saver</p>
                                  <p className="text-sm text-amber-700">{step.highlight}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {step.strategy && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Target className="h-5 w-5 text-purple-600 mt-0.5" />
                                <div>
                                  <p className="font-medium text-purple-800 mb-1">Strategic Approach</p>
                                  <p className="text-sm text-purple-700">{step.strategy}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {step.benefit && (
                            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Sparkles className="h-5 w-5 text-primary mt-0.5" />
                                <div>
                                  <p className="font-medium text-primary mb-1">Key Benefit</p>
                                  <p className="text-sm text-primary/80">{step.benefit}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {step.power && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Zap className="h-5 w-5 text-red-600 mt-0.5" />
                                <div>
                                  <p className="font-medium text-red-800 mb-1">Instant Results</p>
                                  <p className="text-sm text-red-700">{step.power}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {step.insight && (
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <BarChart3 className="h-5 w-5 text-indigo-600 mt-0.5" />
                                <div>
                                  <p className="font-medium text-indigo-800 mb-1">Smart Analytics</p>
                                  <p className="text-sm text-indigo-700">{step.insight}</p>
                                </div>
                              </div>
                            </div>
                           )}
                           
                           {/* Technical Details */}
                           {step.technical && (
                             <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                               <div className="flex items-start gap-3">
                                 <Code className="h-5 w-5 text-slate-600 mt-0.5" />
                                 <div>
                                   <p className="font-medium text-slate-800 mb-2">Technical Implementation</p>
                                   <ul className="space-y-1">
                                     {step.technical.map((tech, i) => (
                                       <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                                         <span className="w-1 h-1 bg-slate-400 rounded-full mt-1.5 flex-shrink-0"></span>
                                         <span>{tech}</span>
                                       </li>
                                     ))}
                                   </ul>
                                 </div>
                               </div>
                             </div>
                           )}
                        </div>
                        
                        {/* Visual/Mockup Side */}
                        <div className="flex-1">
                          <div className="bg-gradient-to-br from-primary/5 to-blue-50 rounded-xl p-8 border border-primary/10">
                            <div className="text-center">
                              <div className="w-24 h-24 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                                {step.icon}
                              </div>
                               <h3 className="text-lg font-semibold mb-2">
                                  {step.step === 1 ? "Actual Google OAuth Screen" : 
                                   step.step === 2 ? "AI Generated Value Prop" :
                                   step.step === 3 ? "Campaign Creation Form" :
                                   step.step === 4 ? "Keyword Research Results" :
                                   step.step === 5 ? "Keyword Analysis & Ad Group Strategy" :
                                   step.step === 6 ? "AI-Generated Ad Headlines & Descriptions" :
                                   step.step === 7 ? "Live Google Ads Campaign Results" :
                                   step.step === 8 ? "Performance Analytics & AI Recommendations" : "Interface Preview"}
                               </h3>
                              <p className="text-sm text-muted-foreground mb-4">{step.mockup}</p>
                               {step.step === 1 ? (
                                 <div className="bg-white rounded-lg p-2 border border-primary/20">
                                   <img 
                                     src="/lovable-uploads/31ee6785-cb16-43b0-b3e9-531a645f2201.png" 
                                     alt="Google OAuth consent screen showing Upfixe ads permissions"
                                     className="w-full h-auto rounded"
                                   />
                                 </div>
                               ) : step.step === 2 ? (
                                 <div className="bg-white rounded-lg p-2 border border-primary/20">
                                   <img 
                                     src="/lovable-uploads/e35e1e7e-10cf-4e75-9fb4-394850eaaaaa.png" 
                                     alt="Brand profile interface showing AI-generated value proposition for HubSpot"
                                     className="w-full h-auto rounded"
                                   />
                                 </div>
                               ) : step.step === 3 ? (
                                 <div className="bg-white rounded-lg p-2 border border-primary/20">
                                   <img 
                                     src="/lovable-uploads/487a42e6-558f-4116-9b35-3ae35858be05.png" 
                                     alt="Campaign creation form interface showing all setup parameters"
                                     className="w-full h-auto rounded"
                                   />
                                 </div>
                               ) : step.step === 4 ? (
                                 <div className="bg-white rounded-lg p-2 border border-primary/20">
                                   <img 
                                     src="/lovable-uploads/1a5e7423-304e-49e4-a0ba-37bddc45df71.png" 
                                     alt="Keyword research results showing search volume, competition, and ROI data"
                                     className="w-full h-auto rounded"
                                   />
                                 </div>
                                 ) : step.step === 5 ? (
                                   <div className="bg-white rounded-lg p-2 border border-primary/20">
                                     <img 
                                       src="/lovable-uploads/2f778e83-dfd8-4ba8-8155-4ef797f3179a.png" 
                                       alt="High ROI keywords and search volume analysis with cost-efficient recommendations"
                                       className="w-full h-auto rounded"
                                     />
                                   </div>
                                  ) : step.step === 6 ? (
                                    <div className="bg-white rounded-lg p-2 border border-primary/20">
                                      <img 
                                        src="/lovable-uploads/9c3831e5-9da2-49a8-b8df-654b43f6cf78.png" 
                                        alt="AI-generated ad headlines and descriptions with character limits and optimization suggestions"
                                        className="w-full h-auto rounded"
                                      />
                                    </div>
                                  ) : step.step === 7 ? (
                                    <div className="bg-white rounded-lg p-2 border border-primary/20">
                                      <img 
                                        src="/lovable-uploads/1161ba30-dd07-4412-981a-ec14ce8e7885.png" 
                                        alt="Live Google Ads showing generated campaigns with headlines, descriptions, and ad groups"
                                        className="w-full h-auto rounded"
                                      />
                                    </div>
                                  ) : step.step === 8 ? (
                                    <div className="bg-white rounded-lg p-2 border border-primary/20">
                                      <img 
                                        src="/lovable-uploads/e798bf6a-7359-4f29-bbdb-c38fcdcace56.png" 
                                        alt="Comprehensive AI insights showing campaign performance, ad group analysis, keyword insights, and strategic overview"
                                        className="w-full h-auto rounded"
                                      />
                                    </div>
                                  ) : (
                                 <div className="bg-white rounded-lg p-4 border border-primary/20">
                                   <div className="space-y-2">
                                     <div className="h-2 bg-primary/20 rounded w-3/4"></div>
                                     <div className="h-2 bg-primary/30 rounded w-1/2"></div>
                                     <div className="h-2 bg-primary/20 rounded w-2/3"></div>
                                   </div>
                                 </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Step Connector */}
                      {index < steps.length - 1 && (
                        <div className="flex justify-center my-8">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <ArrowRight className="h-6 w-6 text-primary" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="user-types" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  {userTypes.map((type, index) => (
                    <Card key={index} className="bg-card border border-primary/10 h-full">
                      <CardHeader className="pb-3">
                        <div className="mb-2">{type.icon}</div>
                        <CardTitle className="text-xl">{type.type}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {type.benefits.map((benefit, i) => (
                            <li key={i} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-primary mt-1 mr-2 flex-shrink-0" />
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Advanced Features */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Advanced Capabilities</Badge>
              <h2 className="text-3xl font-bold mb-4">Supercharged Features</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Beyond basic campaign creation, our platform offers enterprise-grade features for scaling and optimization.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {advancedFeatures.map((feature, index) => (
                <Card key={index} className="bg-card border border-primary/10 h-full hover:shadow-lg transition-all hover:border-primary/20">
                  <CardHeader>
                    <div className="flex items-center mb-2">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                   <CardContent>
                     <p className="text-muted-foreground mb-4">{feature.description}</p>
                     <div className="bg-primary/5 p-3 rounded-lg mb-3">
                       <div className="flex items-start">
                         <Sparkles className="h-4 w-4 text-primary mt-0.5 mr-2 flex-shrink-0" />
                         <p className="text-sm font-medium">{feature.benefit}</p>
                       </div>
                     </div>
                     <div className="bg-slate-50 p-2 rounded-lg">
                       <div className="flex items-start">
                         <Code className="h-3 w-3 text-slate-500 mt-0.5 mr-2 flex-shrink-0" />
                         <p className="text-xs text-slate-600">{feature.technical}</p>
                       </div>
                     </div>
                   </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Technical Benefits */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Technical Advantages</Badge>
              <h2 className="text-3xl font-bold mb-4">Why Our Automation Works</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Professional campaign structure from day one, no Google Ads expertise required.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalBenefits.map((benefit, index) => (
                <Card key={index} className="bg-gradient-to-br from-primary/5 to-blue-50 border border-primary/10">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary/10 p-3 rounded-full">
                        {benefit.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                        <p className="text-muted-foreground mb-3">{benefit.description}</p>
                        <div className="bg-primary/10 px-3 py-1 rounded-full inline-block">
                          <span className="text-sm font-medium text-primary">{benefit.time}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Sample Campaign Structure */}
          <div className="bg-gradient-to-br from-primary/5 to-blue-50 rounded-xl p-8 mb-20 border border-primary/10">
            <div className="text-center mb-8">
              <Badge className="mb-4" variant="secondary">Campaign Example</Badge>
              <h2 className="text-2xl font-bold mb-4">5 Strategic Ad Groups Structure</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                See how our AI organizes keywords into strategic ad groups for maximum performance
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              <Card className="bg-white border border-green-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <CardTitle className="text-sm">High ROI Solutions</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-2">Keywords with historically low CPA</p>
                  <div className="text-xs bg-green-50 p-2 rounded">25-30 keywords focused on cost-effective leads</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-blue-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <CardTitle className="text-sm">High Volume Searches</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-2">Maximum visibility keywords</p>
                  <div className="text-xs bg-blue-50 p-2 rounded">30-40 high search volume terms</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <CardTitle className="text-sm">Native Language Focus</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-2">Core business terminology</p>
                  <div className="text-xs bg-purple-50 p-2 rounded">Brand-specific language and terms</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-orange-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <CardTitle className="text-sm">AI Thematic Group 1</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-2">First AI-generated theme</p>
                  <div className="text-xs bg-orange-50 p-2 rounded">Semantically related keyword clusters</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-red-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <CardTitle className="text-sm">AI Thematic Group 2</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-2">Second thematic grouping</p>
                  <div className="text-xs bg-red-50 p-2 rounded">Additional strategic keyword themes</div>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-6">
              <p className="text-sm text-muted-foreground">
                Each ad group gets 15 headlines + 5 descriptions optimized for its keyword theme
              </p>
            </div>
          </div>

          {/* Before vs After Comparison */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Traditional vs AI-Powered</Badge>
              <h2 className="text-3xl font-bold mb-4">See the Difference</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <Card className="bg-red-50 border border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-800">
                    <Clock className="h-5 w-5" />
                    Traditional Manual Approach
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-red-700">✗ 10+ hours of manual keyword research</p>
                    <p className="text-sm text-red-700">✗ Guesswork for ad group organization</p>
                    <p className="text-sm text-red-700">✗ Writing ad copy from scratch</p>
                    <p className="text-sm text-red-700">✗ Manual campaign setup in Google Ads</p>
                    <p className="text-sm text-red-700">✗ Creating reports manually</p>
                  </div>
                  <Separator />
                  <div className="bg-red-100 p-3 rounded">
                    <p className="text-sm font-medium text-red-800">Total Time: 15-20 hours per campaign</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-green-50 border border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-800">
                    <Zap className="h-5 w-5" />
                    Upfixe AI-Powered Workflow
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-green-700">✓ AI researches 150+ keywords automatically</p>
                    <p className="text-sm text-green-700">✓ Strategic 5-group structure created</p>
                    <p className="text-sm text-green-700">✓ 75 headlines + 25 descriptions generated</p>
                    <p className="text-sm text-green-700">✓ One-click publication to Google Ads</p>
                    <p className="text-sm text-green-700">✓ Automated weekly performance reports</p>
                  </div>
                  <Separator />
                  <div className="bg-green-100 p-3 rounded">
                    <p className="text-sm font-medium text-green-800">Total Time: 5 minutes per campaign</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Bulk Generation Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white" variant="secondary">Supercharged Feature</Badge>
              <h2 className="text-3xl font-bold mb-4">
                Bulk Campaign Generation
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent"> at Scale</span>
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Create comprehensive campaign portfolios from complete website analysis with intelligent page discovery and relevance scoring.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border border-purple-200 mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-purple-800">Website Scanner Technology</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <ScanLine className="h-6 w-6 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-medium mb-1">50-Page Deep Crawling</p>
                        <p className="text-sm text-purple-700">3-level depth analysis discovers all relevant landing pages automatically</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Brain className="h-6 w-6 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-medium mb-1">AI Relevance Scoring</p>
                        <p className="text-sm text-purple-700">Smart page classification: products, services, categories, landing pages</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Workflow className="h-6 w-6 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-medium mb-1">Batch Campaign Creation</p>
                        <p className="text-sm text-purple-700">Generate 10+ campaigns simultaneously with strategic geo/language targeting</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-6 border border-purple-200">
                  <h4 className="font-semibold mb-4 text-center">Bulk Generation Process</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">1</div>
                      <span>Website scan discovers 50 pages</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">2</div>
                      <span>AI scores page relevance</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">3</div>
                      <span>Select targeting combinations</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">4</div>
                      <span>Generate multiple campaigns</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">5</div>
                      <span>Bulk review and publish</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardHeader>
                  <Globe className="h-8 w-8 text-blue-600 mb-2" />
                  <CardTitle className="text-lg">Multi-Market Expansion</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-blue-700">Launch in multiple countries and languages simultaneously with cultural adaptation and local market insights.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardHeader>
                  <Timer className="h-8 w-8 text-green-600 mb-2" />
                  <CardTitle className="text-lg">10x Faster Scaling</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-green-700">Create entire campaign portfolios in minutes instead of weeks of manual campaign setup and optimization.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                <CardHeader>
                  <Lightbulb className="h-8 w-8 text-orange-600 mb-2" />
                  <CardTitle className="text-lg">Smart Page Discovery</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-orange-700">AI identifies the most valuable pages for campaign creation based on content analysis and conversion potential.</p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="text-center mb-8">
              <Badge className="mb-4" variant="secondary">Common Questions</Badge>
              <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-card border border-primary/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary" />
                    How does AI keyword analysis work?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Our AI analyzes your value proposition against 150+ researched keywords, evaluating relevance, 
                    intent match, and conversion potential to ensure campaigns target terms that align with your offering.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-card border border-primary/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lock className="h-5 w-5 text-primary" />
                    Is my Google Ads account secure?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    We use OAuth 2.0 authentication and only request necessary permissions. Your Google credentials 
                    are never permanently stored, and you maintain full control over your account.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-card border border-primary/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-primary" />
                    What makes the 5-group structure effective?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Our strategic grouping separates high-ROI keywords, high-volume terms, brand-specific language, 
                    and AI-generated themes for optimal budget allocation and performance tracking.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-card border border-primary/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Can agencies manage multiple clients?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Yes! Create unlimited brands (paid plans), each with dedicated AI assistants. Support for 
                    Manager (MCC) accounts makes client management seamless.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Button asChild variant="outline" size="lg">
                <Link to="/faqs">View All FAQs</Link>
              </Button>
            </div>
          </div>

          {/* Blog Links Section */}
          <div className="py-20 px-4 bg-blue-50 rounded-2xl my-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Learn Best Practices for Campaign Success</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Discover advanced strategies for optimizing budgets, reducing CPA, and scaling your campaigns effectively.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Complete framework for maximizing ROI on every dollar spent.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to cut your cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/google-ads-for-agencies" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Google Ads for Agencies</h3>
                <p className="text-sm text-muted-foreground mb-4">Scale your client management 10x with intelligent automation.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center">
            <Card className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 to-blue-50 border-primary/20">
              <CardHeader className="text-center pb-6">
                <Badge className="mb-4 mx-auto" variant="secondary">Ready to Start?</Badge>
                <CardTitle className="text-3xl mb-4">
                  Transform Your Google Ads in 
                  <span className="text-primary"> 5 Minutes</span>
                </CardTitle>
                <CardDescription className="text-lg max-w-2xl mx-auto">
                  Join thousands of businesses using AI-powered automation to create professional Google Ads campaigns 
                  without the complexity, time investment, or expertise requirements.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <Button 
                    size="lg"
                    className="px-8"
                    onClick={() => {
                      window.location.href = "https://app.upfixe.com";
                    }}
                  >
                    <Rocket className="mr-2 h-5 w-5" />
                    Start Free Account
                  </Button>
                  <Button variant="outline" asChild size="lg" className="px-8">
                    <Link to="/pricing">
                      <DollarSign className="mr-2 h-5 w-5" />
                      View Pricing
                    </Link>
                  </Button>
                </div>
                
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>No setup fees</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Cancel anytime</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Live in 5 minutes</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default HowItWorks;
